import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cardCls, Btn, Input, Textarea, Field } from "@/components/admin/ui";

const BLANK = { name: "", description: "", price: "", price_active: false };

export default function ServiceFormatsEditor() {
  const [formats, setFormats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setFormats(data.service_formats || [])).catch(() => {});
  }, []);

  const upd = (i, k, v) => setFormats((fs) => fs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const add = () => setFormats((fs) => [...fs, { ...BLANK }]);
  const remove = (i) => setFormats((fs) => fs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = formats.filter((f) => (f.name || "").trim());
      await api.put("/admin/settings", { service_formats: clean });
      setFormats(clean);
      toast.success("Service formats saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="service-formats-editor" className={`${cardCls} p-6 mb-8`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Layers size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Service formats</h2>
        </div>
        <div className="flex gap-3">
          <Btn variant="outline" data-testid="format-add" onClick={add}><Plus size={14} /> Add format</Btn>
          <Btn data-testid="formats-save" onClick={save} disabled={saving}><Save size={14} /> Save</Btn>
        </div>
      </div>
      <p className="text-xs text-bd-ivory/40 mb-5">These appear on the public Services page. Prices stay hidden from the website until you switch a format's price on.</p>

      <div className="space-y-4">
        {formats.map((f, i) => (
          <div key={i} data-testid={`format-row-${i}`} className="border border-white/10 p-4 bg-bd-black/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name"><Input data-testid={`format-name-${i}`} value={f.name} onChange={(e) => upd(i, "name", e.target.value)} /></Field>
              <div className="flex items-end gap-3">
                <div className="flex-1"><Field label="Price (optional)"><Input data-testid={`format-price-${i}`} value={f.price || ""} onChange={(e) => upd(i, "price", e.target.value)} placeholder="e.g. From £1,500" /></Field></div>
                <button type="button" data-testid={`format-price-toggle-${i}`} onClick={() => upd(i, "price_active", !f.price_active)} role="switch" aria-checked={!!f.price_active}
                  className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mb-1 ${f.price_active ? "bg-bd-gold" : "bg-white/15"}`} title={f.price_active ? "Price shown on site" : "Price hidden"}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-bd-black transition-transform ${f.price_active ? "translate-x-6" : ""}`} />
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <div className="flex-1"><Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => upd(i, "description", e.target.value)} /></Field></div>
              <button data-testid={`format-remove-${i}`} onClick={() => remove(i)} className="mt-6 p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {formats.length === 0 && <p className="text-sm text-bd-ivory/40 text-center py-6">No formats yet. Add one to show it on the website.</p>}
      </div>
    </div>
  );
}
